import { 
  ArrowRight, BarChart3, Bell, CheckCircle2, CreditCard, Package, 
  ShoppingBag, Smartphone, Store, Globe, ShieldCheck
} from 'lucide-react';
import { Link } from 'react-router-dom';
import React from 'react'; // ✅ OPTIMIZACIÓN: Para React.memo

// --- CAMBIO AQUÍ: Usamos NavbarPublic en lugar de Navbar ---
import { NavbarPublic } from '../../components/NavbarPublic.jsx'; 
import { prefetchRoute } from '../../utils/prefetch.js'; // ✅ OPTIMIZACIÓN: Prefetch para navegación
import { useScrollAnimation } from '../../hooks/useScrollAnimation.js'; // ✅ Animaciones on scroll
import heroImage from '../../assets/hero.webp'; // ✅ Hero image import

// --- FONDO AMBIENTAL OPTIMIZADO ---
const AmbientBackground = React.memo(() => (
  <div className="fixed inset-0 -z-10 overflow-hidden pointer-events-none bg-[#020202]">
    {/* ✅ OPTIMIZACIÓN: Blur reducido de 150px → 60px para mejor GPU performance */}
    <div className="absolute top-[-10%] left-[-10%] w-[70vw] h-[70vw] bg-indigo-900/20 rounded-full blur-[60px] opacity-40 mix-blend-multiply animate-pulse" style={{ animationDuration: '10s' }} />
    {/* ✅ OPTIMIZACIÓN: Blur reducido de 150px → 80px */}
    <div className="absolute bottom-[-10%] right-[-10%] w-[60vw] h-[60vw] bg-cyan-900/10 rounded-full blur-[80px] opacity-30 mix-blend-multiply" />
    {/* ✅ OPTIMIZACIÓN: Blur reducido de 120px → 50px */}
    <div className="absolute top-[40%] left-[40%] w-[40vw] h-[40vw] bg-purple-900/10 rounded-full blur-[50px] opacity-20 mix-blend-multiply" />
  </div>
));

// ✅ OPTIMIZACIÓN: Componente de característica memoizado
const FeatureCard = React.memo(({ feature, idx }) => {
  const handleMouseEnter = () => {
    // Prefetch inteligente basado en la característica
    if (feature.title === "Catálogo Inteligente") {
      prefetchRoute("/panel/productos");
    } else if (feature.title === "Pagos Flexibles") {
      prefetchRoute("/panel/configuracion");
    }
  };

  return (
    <div 
      key={idx} 
      className="group p-8 rounded-[2rem] bg-[#0A0A0A] border border-white/5 hover:border-white/10 transition-colors hover:translate-y-[-2px] duration-300"
      onMouseEnter={handleMouseEnter}
    >
       <div className={`w-14 h-14 rounded-2xl ${feature.bg} border border-white/5 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300`}>
          <feature.icon className={`h-7 w-7 ${feature.color}`} />
       </div>
       <h3 className="text-xl font-bold text-white mb-3">{feature.title}</h3>
       <p className="text-slate-400 text-sm leading-relaxed">{feature.desc}</p>
    </div>
  );
});

// ✅ OPTIMIZACIÓN: Componente de plan memoizado
const PricingCard = React.memo(({ plan, isPopular = false }) => {
  const handleMouseEnter = () => {
    prefetchRoute("/registro/tienda");
  };

  return (
    <div 
      className={`relative rounded-[2rem] border ${isPopular ? 'border-indigo-500/50 bg-[#0F0F0F]/80 shadow-[0_0_40px_rgba(79,70,229,0.15)] transform md:translate-y-[-16px]' : 'border-white/5 bg-[#0A0A0A] hover:border-white/20'} p-8 transition-colors h-full flex flex-col`}
      onMouseEnter={handleMouseEnter}
    >
      {isPopular && (
        <div className="absolute -top-4 left-1/2 transform translate-x-[-50%] px-4 py-1 bg-indigo-500 text-white text-[10px] font-bold uppercase tracking-widest rounded-full">Más Popular</div>
      )}
      <h3 className="text-xl font-bold text-white mb-2">{plan.name}</h3>
      <div className={`${isPopular ? 'text-4xl' : 'text-3xl'} font-bold text-white mb-1`}>
        ${plan.price} <span className="text-sm text-slate-500 font-normal">/mes</span>
      </div>
      {plan.subtitle && (
        <p className="text-xs text-indigo-400 mb-6 uppercase tracking-widest">{plan.subtitle}</p>
      )}
      <ul className="space-y-4 mb-8 flex-grow">
         {plan.features.map(item => (
            <li key={item} className={`flex items-center gap-3 text-sm ${isPopular ? 'text-slate-300' : 'text-slate-400'}`}>
               <CheckCircle2 className={`h-4 w-4 ${isPopular ? 'text-indigo-400' : plan.name === 'Pro' ? 'text-purple-400' : 'text-white/20'}`} /> {item}
            </li>
         ))}
      </ul>
      <Link 
        to="/registro/tienda" 
        className={`block w-full py-${isPopular ? '4' : '3'} rounded-xl ${isPopular ? 'bg-indigo-600 hover:bg-indigo-500 shadow-lg' : plan.name === 'Pro' ? 'border border-white/10 bg-white/5 hover:bg-purple-500/20 hover:border-purple-500/50' : 'border border-white/10 bg-white/5 hover:bg-white/10'} text-center text-sm font-bold uppercase tracking-widest text-white transition-colors`}
      >
        {plan.cta}
      </Link>
    </div>
  );
});

export function LandingPage() {
  // Refs para animaciones on scroll
  const [heroRef, heroVisible] = useScrollAnimation({ threshold: 0.2 });
  const [statsRef, statsVisible] = useScrollAnimation({ threshold: 0.3 });
  const [featuresRef, featuresVisible] = useScrollAnimation({ threshold: 0.2 });
  const [pricingRef, pricingVisible] = useScrollAnimation({ threshold: 0.2 });
  
  return (
    <div className="min-h-screen text-slate-200 selection:bg-indigo-500/30 font-sans">
      <AmbientBackground />
      
      {/* Usamos la barra pública que tiene Carrito y Marketplace */}
      <NavbarPublic />
      
      {/* HERO SECTION COMPACTA */}
      {/* CORRECCIÓN APLICADA: Aumentado pt-16 -> pt-32 y lg:pt-20 -> lg:pt-40 para evitar choque con Navbar */}
      <main 
        ref={heroRef}
        className={`relative pt-32 pb-12 lg:pt-40 lg:pb-20 px-6 overflow-hidden transition-all duration-700 ${
          heroVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
        }`}
      >
        <div className="mx-auto max-w-7xl grid lg:grid-cols-2 gap-8 lg:gap-16 items-center">
          
          {/* Texto Hero */}
          <div className="relative z-10 flex flex-col justify-center">
            
            {/* Badge */}
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-cyan-500/30 bg-cyan-500/10 backdrop-blur-sm mb-4">
                <span className="flex h-2 w-2 rounded-full bg-cyan-400 animate-pulse"></span>
                <span className="text-[10px] font-bold uppercase tracking-widest text-cyan-400">
                  Nueva Generación de E-commerce
                </span>
              </div>
            </div>
            
            {/* Título */}
            <h1 className="text-5xl lg:text-7xl font-bold text-white tracking-tight leading-[1.1] mb-4">
              Vende sin <br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-indigo-400 via-cyan-400 to-emerald-400">
                Límites.
              </span>
            </h1>
            
            <p className="text-lg text-slate-400 max-w-xl leading-relaxed mb-8">
              La plataforma definitiva para gestionar tu tienda, pedidos y pagos. 
              Sin complicaciones técnicas. Diseño premium. Potencia absoluta.
            </p>
            
            <div className="flex flex-wrap gap-4">
              <Link
                to="/registro/tienda"
                className="inline-flex items-center gap-3 rounded-full bg-white text-black px-8 py-4 text-sm font-bold uppercase tracking-widest hover:bg-slate-200 transition-colors transform hover:scale-105 shadow-[0_0_20px_rgba(255,255,255,0.3)]"
                onMouseEnter={() => prefetchRoute("/registro/tienda")}
              >
                Crear Tienda <ArrowRight className="h-4 w-4" />
              </Link>
              <Link
                to="/marketplace"
                className="inline-flex items-center gap-3 rounded-full border border-white/10 bg-white/5 px-8 py-4 text-sm font-bold uppercase tracking-widest text-white hover:bg-white/10 transition-colors backdrop-blur-md"
                onMouseEnter={() => prefetchRoute("/marketplace")}
              >
                Explorar Market
              </Link>
            </div>
          </div>

          {/* Imagen Hero */}
          <div className="relative group mt-8 lg:mt-0">
            <div className="absolute -inset-1 bg-gradient-to-r from-indigo-500 to-cyan-500 rounded-[2rem] blur opacity-30 group-hover:opacity-50 transition-opacity duration-1000"></div>
            <div className="relative rounded-[2rem] bg-gradient-to-br from-[#1a1a1a] via-[#0F0F0F] to-[#1a1a1a] border border-white/10 overflow-hidden shadow-2xl transform transition-transform duration-500 group-hover:rotate-1">
              <img 
                src={heroImage} 
                alt="Dashboard TiendasYA" 
                className="w-full h-auto object-cover"
                loading="eager"
              />
            </div>
          </div>
        </div>
      </main>

      {/* MARKETPLACE BANNER */}
      <section className="py-16 relative">
        <div className="absolute inset-0 bg-gradient-to-r from-indigo-900/20 to-purple-900/20" />
        <div className="mx-auto max-w-5xl px-6 relative z-10">
           <div className="rounded-[2.5rem] border border-white/10 bg-[#0F0F0F]/80 backdrop-blur-xl p-10 md:p-12 text-center overflow-hidden group">
              <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-cyan-500 to-transparent opacity-50" />
              
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
                Un Universo de Productos
              </h2>
              <p className="text-slate-400 text-lg max-w-2xl mx-auto mb-8">
                Accede al Marketplace global. Miles de tiendas conectadas en una sola red. 
                Compra seguro y descubre marcas locales con calidad premium.
              </p>
              
              <Link
                to="/marketplace"
                className="inline-flex items-center gap-3 rounded-full bg-gradient-to-r from-indigo-600 to-cyan-600 px-8 py-4 text-sm font-bold uppercase tracking-widest text-white shadow-[0_0_30px_rgba(79,70,229,0.4)] hover:shadow-[0_0_40px_rgba(79,70,229,0.6)] hover:scale-105 transition-all duration-300"
                onMouseEnter={() => prefetchRoute("/marketplace")}
              >
                <ShoppingBag className="h-5 w-5" /> Ir al Marketplace
              </Link>
           </div>
        </div>
      </section>

      {/* ESTADÍSTICAS REALES (Beneficios) */}
      <section 
        ref={statsRef}
        className={`py-10 border-y border-white/5 bg-black/20 transition-all duration-700 ${
          statsVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
        }`}
      >
        <div className="mx-auto max-w-6xl px-6">
          <div className="grid gap-10 md:grid-cols-3 text-center divide-y md:divide-y-0 md:divide-x divide-white/5">
            <div className="pt-4 md:pt-0">
              <div className="text-2xl md:text-3xl font-bold text-white mb-2 tracking-tight">⚡ SETUP RÁPIDO</div>
              <p className="text-[10px] font-bold uppercase tracking-widest text-slate-500">Tienda lista en minutos</p>
            </div>
            <div className="pt-8 md:pt-0">
              <div className="text-2xl md:text-3xl font-bold text-white mb-2 tracking-tight">🛒 TODO INCLUIDO</div>
              <p className="text-[10px] font-bold uppercase tracking-widest text-slate-500">Catálogo + Pagos + Envíos</p>
            </div>
            <div className="pt-8 md:pt-0">
              <div className="text-2xl md:text-3xl font-bold text-white mb-2 tracking-tight">💰 DESDE $10/MES</div>
              <p className="text-[10px] font-bold uppercase tracking-widest text-slate-500">Sin costos ocultos</p>
            </div>
          </div>
        </div>
      </section>

      {/* CARACTERÍSTICAS (Grid Luxury) */}
      <section 
        ref={featuresRef}
        className={`py-24 px-6 transition-all duration-700 ${
          featuresVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
        }`}
      >
        <div className="mx-auto max-w-7xl">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-5xl font-bold text-white mb-4 tracking-tight">
              Tecnología para el Comercio Moderno
            </h2>
            <p className="text-slate-400 text-lg">
              Herramientas de vanguardia diseñadas para escalar tu negocio.
            </p>
          </div>

          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
             {[
                { icon: ShoppingBag, title: "Catálogo Inteligente", desc: "Productos con variantes automáticas, control de stock en tiempo real y promociones dinámicas.", color: "text-cyan-400", bg: "bg-cyan-400/10" },
                { icon: Bell, title: "Alertas Tiempo Real", desc: "Notificaciones push instantáneas en tu dispositivo. Email y Telegram disponibles.", color: "text-orange-400", bg: "bg-orange-400/10" },
                { icon: CreditCard, title: "Pagos Múltiples", desc: "Transferencia, Pago Móvil, Zelle, PayPal, Binance y Criptomonedas configurables.", color: "text-emerald-400", bg: "bg-emerald-400/10" },
                { icon: Package, title: "Gestión Completa", desc: "Retiro en tienda, delivery local y envíos nacionales con seguimiento completo.", color: "text-indigo-400", bg: "bg-indigo-400/10" },
                { icon: BarChart3, title: "Reportes Reales", desc: "KPIs en tiempo real, clasificación de clientes y análisis de ganancias exportables.", color: "text-pink-400", bg: "bg-pink-400/10" },
                { icon: Smartphone, title: "App Instalable (PWA)", desc: "Instala TiendasYA en tu dispositivo como una app nativa. Funciona offline y recibe notificaciones.", color: "text-white", bg: "bg-white/10" },
             ].map((feature, idx) => (
                <FeatureCard key={idx} feature={feature} idx={idx} />
             ))}
          </div>
        </div>
      </section>

      {/* PLANES DE PRECIOS (Monolitos) */}
      <section 
        ref={pricingRef}
        className={`py-20 px-6 relative transition-all duration-700 ${
          pricingVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
        }`}
      >
         <div className="mx-auto max-w-6xl">
            <div className="text-center mb-16">
               <h2 className="text-4xl font-bold text-white mb-4 tracking-tight">Planes de Suscripción</h2>
               <p className="text-slate-400">Escala tu negocio con precios transparentes.</p>
            </div>

            <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-4 items-start">
               <PricingCard 
                 plan={{
                   name: "Prueba Gratis",
                   price: 0,
                   subtitle: "15 días sin compromiso",
                   features: ['Hasta 50 productos', '100 Pedidos de Prueba', 'Acceso Completo por 15 Días', 'Notificaciones Push + Telegram'],
                   cta: "Empezar Gratis"
                 }}
               />
               
               <PricingCard 
                 plan={{
                   name: "Básico",
                   price: 10,
                   subtitle: "Para emprendedores",
                   features: ['200 Productos', '500 Pedidos/mes', 'Notificaciones Push + Telegram', 'Reportes Avanzados', 'Gestionar Clientes'],
                   cta: "Elegir Plan"
                 }}
               />

               <PricingCard 
                 plan={{
                   name: "Pro",
                   price: 25,
                   features: ['1,000 Productos', '2,000 Pedidos/mes', 'Notificaciones Push + Telegram + Email', 'Reportes Avanzados', 'Gestionar Clientes'],
                   cta: "Elegir Plan"
                 }}
                 isPopular={true}
               />

               <PricingCard 
                 plan={{
                   name: "Premium",
                   price: 50,
                   features: ['Productos Ilimitados', 'Pedidos Ilimitados', 'Notificaciones Push + Telegram + Email', 'Reportes Avanzados', 'Gestionar Clientes'],
                   cta: "Contactar Ventas"
                 }}
               />
            </div>
         </div>
      </section>

      {/* FOOTER SIMPLE */}
      <footer className="border-t border-white/10 bg-black py-12 px-6">
         <div className="mx-auto max-w-6xl flex flex-col md:flex-row justify-between items-center gap-6">
            <div className="flex items-center gap-2">
               <div className="p-2 bg-white/10 rounded-full"><Store className="h-5 w-5 text-white" /></div>
               <span className="text-lg font-bold tracking-widest uppercase text-white">TiendasYA</span>
            </div>
            <p className="text-slate-500 text-sm">© 2025 TiendasYA. Design by Chronoswiss Style.</p>
            <div className="flex gap-6">
               <a href="#" className="text-slate-500 hover:text-white transition-colors"><Globe className="h-5 w-5"/></a>
               <a href="#" className="text-slate-500 hover:text-white transition-colors"><ShieldCheck className="h-5 w-5"/></a>
            </div>
         </div>
      </footer>

    </div>
  );
}